/**
 * Toast ID constants for showToast().
 * All toast calls must use an id from this config so the same id replaces the previous toast.
 * Add new IDs here when adding new toast scenarios.
 */
export const TOAST_IDS = {
  // Internal Pattern Studio
  PATTERN_STUDIO_DOWNLOAD: 'pattern-studio-download',
  PATTERN_STUDIO_IMPORT: 'pattern-studio-import',
  PATTERN_STUDIO_CLIPBOARD_READ: 'pattern-studio-clipboard-read',
  PATTERN_STUDIO_CONFIGURATION: 'pattern-studio-configuration',
  PATTERN_STUDIO_CLIPBOARD_WRITE: 'pattern-studio-clipboard-write',
  // Auth
  AUTH_WELCOME: 'auth-welcome',
  AUTH_ACCOUNT_CREATED: 'auth-account-created',
  AUTH_LOGIN_SUCCESS: 'auth-login-success',
  AUTH_SIGNUP_SUCCESS: 'auth-signup-success',
  AUTH_SENDING_OTP: 'auth-sending-otp',
  AUTH_LOGOUT: 'auth-logout',
  AUTH_LOGOUT_LOADING: 'auth-logout-loading',
  AUTH_SESSION_EXPIRED: 'auth-session-expired',
  AUTH_CALLBACK_ERROR: 'auth-callback-error',
  AUTH_ERROR: 'auth-error',
  AUTH_SYNCING: 'auth-syncing',
  CLEAR_CACHE_ERROR: 'clear-cache-error',

  // Wallet
  WALLET_SWITCH: 'wallet-switch',
  WALLET_CREATED: 'wallet-created',
  WALLET_CONNECTED: 'wallet-connected',
  WALLET_SWITCHED: 'wallet-switched',
  WALLET_RENAMED: 'wallet-renamed',
  WALLET_REMOVED: 'wallet-removed',
  WALLET_ARCHIVED: 'wallet-archived',
  WALLET_ADDRESS_COPIED: 'wallet-address-copied',
  WALLET_RESTORED: 'wallet-restored',
  BALANCE_ERROR: 'balance-error',
  WALLET_EXPORTED: 'wallet-exported',
  WALLET_EXPORT_FAILED: 'wallet-export-failed',
  WALLET_IMPORTED: 'wallet-imported',
  WALLET_IMPORT_LOADING: 'wallet-import-loading',
  WALLET_ERROR: 'wallet-error',
  TRANSFER_SUBMITTING: 'transfer-submitting',
  TRANSFER_SUCCESS: 'transfer-success',
  TRANSFER_FAILED: 'transfer-failed',

  // Locale
  LOCALE_CHANGED: 'locale-changed',

  // Location
  LOCATION_UPDATED: 'location-updated',

  // Currency
  CURRENCY_UPDATED: 'currency-updated',

  // Profile
  PROFILE_UPDATED: 'profile-updated',
  PROFILE_ERROR: 'profile-error',
  PROFILE_LINK_COPIED: 'profile-link-copied',
  MORFI_HANDLE_COPY: 'morfi-handle-copy',
  ACCOUNT_DELETED: 'account-deleted',
  LOGGED_OUT: 'logged-out',

  // Transaction (T064) -- prefixes; runtime appends _${traceId}
  TX_SUBMITTING: 'tx-submitting',
  TX_CONFIRMED: 'tx-confirmed',
  TX_FAILED: 'tx-failed',
  TX_RETRY: 'tx-retry',
  TX_CANCELLED: 'tx-cancelled',
  TX_RETRY_COUNTDOWN: 'tx-retry-countdown',

  // Chart
  CHART_LEFT_EDGE: 'chart-left-edge',
  CHART_LIVE_EDGE: 'chart-live-edge',

  // Faucet — single id across loading/success/error so the result toast
  // replaces the in-progress "Claiming test funds…" one in place.
  FAUCET_CLAIM: 'faucet-claim',

  // Feedback & Reports — single id so the result replaces in place.
  FEEDBACK_RESULT: 'feedback-result',
  SUPPORT_OPEN: 'support-open',

  // Search
  SEARCH_EXACT_MATCH: 'search-exact-match',

  // Market detail
  SHARE_LINK_COPIED: 'share-link-copied',
  TICKER_COPIED: 'ticker-copied',

  // Early-access waitlist
  WAITLIST_SUBSCRIBED: 'waitlist-subscribed',
  WAITLIST_OTP_RESENT: 'waitlist-otp-resent',
  REWARDS_EMAIL_VERIFIED: 'rewards-email-verified',
  WAITLIST_EMAIL_REJECTED: 'waitlist-email-rejected',
  REWARDS_SIGN_IN_REQUIRED: 'rewards-sign-in-required',
  REWARDS_CONTENT_SUBMITTED: 'rewards-content-submitted',
  REWARDS_POST_COPIED: 'rewards-post-copied',
  REWARDS_CLIP_COPIED: 'rewards-clip-copied',
  REWARDS_CLIP_DOWNLOADED: 'rewards-clip-downloaded',
  REWARDS_QUEST_COMPLETED: 'rewards-quest-completed',
  REWARDS_STREAK_CLAIMED: 'rewards-streak-claimed',
  REWARDS_X_CONNECT_ERROR: 'rewards-x-connect-error',

  // Boost
  BOOST_SUCCESS: 'boost-success',
  BOOST_ERROR: 'boost-error',

  // Launchpad proposals (T025)
  PROPOSAL_VOTED: 'proposal-voted',
  PROPOSAL_VOTE_CLEARED: 'proposal-vote-cleared',
  PROPOSAL_VOTE_ERROR: 'proposal-vote-error',
  PROPOSAL_CREATED: 'proposal-created',
  PROPOSAL_CREATE_ERROR: 'proposal-create-error',
  PROPOSAL_BACKED: 'proposal-backed',
  PROPOSAL_BACK_ERROR: 'proposal-back-error',
  PROPOSAL_WITHDRAW_SUBMITTING: 'proposal-withdraw-submitting',
  PROPOSAL_WITHDRAWN: 'proposal-withdrawn',
  PROPOSAL_WITHDRAW_ERROR: 'proposal-withdraw-error',
  PROPOSAL_WITHDRAW_CANCELLED: 'proposal-withdraw-cancelled',
  PROPOSAL_APPROVED: 'proposal-approved',
  PROPOSAL_APPROVE_ERROR: 'proposal-approve-error',
  PROPOSAL_REJECTED: 'proposal-rejected',
  PROPOSAL_REJECT_ERROR: 'proposal-reject-error',
  PROPOSAL_INSUFFICIENT_BALANCE: 'proposal-insufficient-balance',
  PROPOSAL_STEP_APPROVE: 'proposal-step-approve',
  PROPOSAL_STEP_CREATE_MARKET: 'proposal-step-create-market',
  PROPOSAL_STEP_GROUP_MINT: 'proposal-step-group-mint',
  PROPOSAL_STEP_FUND: 'proposal-step-fund',
  PROPOSAL_STEP_OUTCOMES: 'proposal-step-outcomes',
  PROPOSAL_STEP_METADATA: 'proposal-step-metadata',
  PROPOSAL_STEP_DISTRIBUTE: 'proposal-step-distribute',
  PROPOSAL_STEP_COMPLETE: 'proposal-step-complete',
  PROPOSAL_STEP_ERROR: 'proposal-step-error',
  PROPOSAL_ID_COPIED: 'proposal-id-copied',
  MARKET_CANCELLED: 'market-cancelled',
  MARKET_CANCEL_ERROR: 'market-cancel-error',

  // Image upload — single id across loading/success/error so the result toast
  // replaces the in-progress one in place.
  IMAGE_UPLOAD: 'image-upload',
  IMAGE_UPLOAD_SUCCESS: 'image-upload-success',
  IMAGE_UPLOAD_ERROR: 'image-upload-error',
  // Prefix — runtime appends a per-slot suffix (e.g. `_cover`, `_outcome-0`) so
  // the cover and each outcome can surface their own failed-image toast.
  IMAGE_LOAD_FAILED: 'image-load-failed',

  // Trade form
  SELL_UNAVAILABLE: 'sell-unavailable',

  // Simulation (012-market-simulation)
  SIMULATION_TRADE_SUBMITTED: 'simulation-trade-submitted',
  SIMULATION_PRIZE_CLAIMED: 'simulation-prize-claimed',
  SIMULATION_HOLDINGS_SOLD: 'simulation-holdings-sold',

  // Claim prize
  CLAIM_PRIZE_SUCCESS: 'claim-prize-success',
  CLAIM_PRIZE_ERROR: 'claim-prize-error',

  // Thesis (003-add-thesis)
  THESIS_SUBMITTING: 'thesis-submitting',
  THESIS_CONFIRMED: 'thesis-confirmed',
  THESIS_FAILED: 'thesis-failed',

  // Watchlist (markets)
  WATCHLIST_SAVING: 'watchlist-saving',
  WATCHLIST_SAVED: 'watchlist-saved',
  WATCHLIST_REMOVED: 'watchlist-removed',
  WATCHLIST_ERROR: 'watchlist-error',

  // Watchlist (proposals)
  PROPOSAL_WATCHLIST_SAVING: 'proposal-watchlist-saving',
  PROPOSAL_WATCHLIST_SAVED: 'proposal-watchlist-saved',
  PROPOSAL_WATCHLIST_REMOVED: 'proposal-watchlist-removed',
  PROPOSAL_WATCHLIST_ERROR: 'proposal-watchlist-error',

  // Follows
  FOLLOW_ACTION: 'follow-action',
  UNFOLLOW_ACTION: 'unfollow-action',
  FOLLOW_ERROR: 'follow-error',

  // Connected accounts (016-account-linking)
  PROVIDER_CONNECTING: 'provider-connecting',
  PROVIDER_LOADING_PROFILE: 'provider-loading-profile',
  PROVIDER_CONNECTED: 'provider-connected',
  PROVIDER_CONNECT_ERROR: 'provider-connect-error',
  PROVIDER_DISCONNECTED: 'provider-disconnected',
  PROVIDER_DISCONNECT_ERROR: 'provider-disconnect-error',
  // X connect — backend-mediated flow (separate from Turnkey login)
  X_CONNECT_SUCCESS: 'x-connect-success',
  X_CONNECT_ERROR: 'x-connect-error',

  // Social (016-social-page)
  REFERRAL_LINK_COPIED: 'referral-link-copied',
  REFERRAL_LINK_SHARED: 'referral-link-shared',
  THESIS_REPLY_SUBMITTED: 'thesis-reply-submitted',
  THESIS_REPLY_DELETED: 'thesis-reply-deleted',
  THESIS_REPORTED: 'thesis-reported',
  THESIS_LINK_COPIED: 'thesis-link-copied',
  THESIS_REACTED: 'thesis-reacted',
  RANK_SHARED: 'rank-shared',

  // Gifts (006-handle-gifting)
  GIFT_SENDING: 'gift-sending',
  GIFT_SENT: 'gift-sent',
  GIFT_SEND_FAILED: 'gift-send-failed',
  GIFT_LINK_COPIED: 'gift-link-copied',
  GIFT_CLAIMING: 'gift-claiming',
  GIFT_CLAIMED: 'gift-claimed',
  GIFT_CLAIM_FAILED: 'gift-claim-failed',
  GIFT_WITHDRAWING: 'gift-withdrawing',
  GIFT_WITHDRAWN: 'gift-withdrawn',
  GIFT_WITHDRAW_FAILED: 'gift-withdraw-failed',
  GIFT_NOTIFICATION: 'gift-notification',

  // Support-mainnet
  DONATION_ADDRESS_COPIED: 'donation-address-copied',

  // Impersonation (engineer-only — spec 048)
  IMPERSONATION_EXPIRED: 'impersonation-expired',
  IMPERSONATION_STOPPED: 'impersonation-stopped',

  // Rate limiting (HTTP 429 from backend)
  RATE_LIMITED: 'rate-limited',

  // Workspace QA — used only by the dev-only /workspace route to demo each
  // toast variant. Not used in production code.
  WORKSPACE_TOAST_DEMO: 'workspace-toast-demo',

  // Pattern studio — dev-only /workspace playground for the bake-at-upload
  // dither pipeline.
  PATTERN_STUDIO_SEEDS_COPIED: 'pattern-studio-seeds-copied',
  PATTERN_STUDIO_EXPORT_FAILED: 'pattern-studio-export-failed',

  // AI Proposal Draft (spec 007) — Generate-tab streaming + per-image retry
  aiProposalGenerateError: 'ai-proposal-generate-error',
  aiProposalRetryImageError: 'ai-proposal-retry-image-error',
  aiProposalNetworkError: 'ai-proposal-network-error',
  // Manual-flow per-module suggest success — single toast id per surface so
  // rapid re-clicks dedupe instead of stacking.
  aiProposalDescriptionSuccess: 'ai-proposal-description-success',
  aiProposalOutcomesSuccess: 'ai-proposal-outcomes-success',
  // Spec 007 / T168 — surfaces 429 rate-limited responses with the reset
  // timestamp parsed from the AI envelope's `details.reset` field. Same
  // toast id is used for both the SSE consumer and every SDK mutation
  // hook so concurrent 429s deduplicate (Sonner replaces by id).
  aiProposalRateLimitedError: 'ai-proposal-rate-limited-error',
  // Suggest-outcome wizard submit failures (non-duplicate path — duplicates
  // are surfaced via the inline DuplicateErrorBanner with an upvote CTA).
  suggestOutcomeSubmitError: 'suggest-outcome-submit-error',
  // Outcome-suggestion vote arrows — mirrors PROPOSAL_VOTED for the
  // launchpad-suggestion leaderboard.
  OUTCOME_SUGGESTION_VOTED: 'outcome-suggestion-voted',
  OUTCOME_SUGGESTION_VOTE_CLEARED: 'outcome-suggestion-vote-cleared',
  OUTCOME_SUGGESTION_VOTE_ERROR: 'outcome-suggestion-vote-error',

  // Brand assets
  MORFI_LOGO_SVG_COPIED: 'morfi-logo-svg-copied',
  MORFI_LOGO_PNG_COPIED: 'morfi-logo-png-copied',
  MORFI_LOGO_COPY_FAILED: 'morfi-logo-copy-failed',
} as const;

export type ToastId = (typeof TOAST_IDS)[keyof typeof TOAST_IDS];
